import { useQuery } from '@tanstack/react-query'
import {
  EducatorHomepageService,
  type HomepageResponse,
} from '@mc/educator-api'

export const EDUCATOR_HOMEPAGE_KEY = ['educator-homepage'] as const

// Five minutes. The homepage shows progress that changes as the user
// completes topics in the Executive Chat — `useExecutiveChat`'s
// `onUserProgress` invalidates this query directly so progress updates feel
// instant. The 5m staleTime is just a safety net for users who navigate
// back to the homepage after a long break.
const STALE_TIME_MS = 5 * 60 * 1000

interface UseEducatorHomepageOptions {
  initialData?: HomepageResponse
  enabled?: boolean
}

export const useEducatorHomepage = (options?: UseEducatorHomepageOptions) =>
  useQuery<HomepageResponse>(
    EDUCATOR_HOMEPAGE_KEY,
    () => EducatorHomepageService.getHomepage(),
    {
      initialData: options?.initialData,
      enabled: options?.enabled,
      staleTime: STALE_TIME_MS,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      retry: (failureCount, error: unknown) => {
        const statusCode =
          error && typeof error === 'object' && 'status' in error
            ? (error as { status: number }).status
            : undefined

        // 401 = unauthorized, 403 = forbidden — neither is recoverable by retry.
        if (statusCode === 401 || statusCode === 403) {
          return false
        }
        return failureCount < 3
      },
    },
  )
